import React, { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import PageNotFound from '@/lib/PageNotFound';

const Loading = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-background" role="status" aria-live="polite">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" aria-label="Loading application" />
  </div>
);

/** Restricts provider-only routes using the server-confirmed AuthContext identity. */
export default function ProviderRoute({ children }) {
  const { user, isAuthenticated, isLoadingAuth, authChecked } = useAuth();
  const [networkAccess, setNetworkAccess] = useState(null);

  useEffect(() => {
    if (isLoadingAuth || !authChecked || !isAuthenticated || !user?.is_provider) return undefined;

    const controller = new AbortController();
    setNetworkAccess(null);
    fetch('/api/v1/admin/access', { credentials: 'include', cache: 'no-store', signal: controller.signal })
      .then((response) => { if (!controller.signal.aborted) setNetworkAccess(response.ok); })
      .catch(() => { if (!controller.signal.aborted) setNetworkAccess(false); });
    return () => controller.abort();
  }, [authChecked, isAuthenticated, isLoadingAuth, user?.is_provider]);

  if (isLoadingAuth || !authChecked) return <Loading />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user?.is_provider) return <PageNotFound />;
  if (networkAccess === null) return <Loading />;
  if (!networkAccess) return <PageNotFound />;

  return children || <Outlet />;
}
